import { ZCTA_POPULATION } from "../../zcta-population.js";
import { ZIP_COUNTY } from "../../zip-county.js";

export { DEFAULT_MAPBOX_TOKEN, MAPBOX_GL_CSS, MAPBOX_GL_JS } from "../mapbox-config.js";

export const MAPBOX_REVERSE_GEOCODE_URL = "https://api.mapbox.com/search/geocode/v6/reverse";

export const MAPBOX_FORWARD_GEOCODE_URL = "https://api.mapbox.com/search/geocode/v6/forward";

export const LOCAL_ARIZONA_ZCTAS_URL = "./data/arizona-zctas.geojson";

export const LOCAL_ARIZONA_COUNTIES_URL = "./data/arizona-counties.geojson";
// PREVIEW (Map Lab): parts of AZ inside no ZCTA at all (Grand Canyon, wilderness, large
// tribal/military land — ~20% of the state). Precomputed offline as state minus ZCTA
// union; see backend/scripts/generate_non_zip_areas.py.

export const LOCAL_ARIZONA_NON_ZIP_URL = "./data/arizona-non-zip-areas.geojson";
// ZCTAs of the neighboring states inside the map's pannable area, generalized to ~500 m
// (they are dimmed background context, never data). Drawn as thin lines under the
// spotlight veil so ZIP coverage visibly continues past the state line; see
// backend/scripts/generate_neighbor_zctas.py.

export const LOCAL_NEIGHBOR_ZCTAS_URL = "./data/neighbor-zctas.geojson";
// PREVIEW (Map Lab dataset): temporary wiring so the generated static dataset
// can be eyeballed on the homepage map. Remove when the Phase 2 map surface lands.

export const MAP_LAB_DATASET_URL = "./data/map-lab-reports.json";

export const MAP_LAB_POINT_COLORS = { animals: "#F0A202", environment: "#0E8A5F" };

// Outbreak dots (see src/map/pie.js): per-ZIP report dots baked as a single sprite drawn
// on one GPU symbol layer. Every dot is solid red (symptom reports) and sized off a step
// ladder that only promotes ZIPs well above the typical count drawn; ZIPs with no symptom
// reports draw nothing. No DOM markers — icon-image picks the baked sprite, icon-size
// scales it.

export const MAP_LAB_PIE_LAYER_ID = "people-pie-dots";

export const MAP_LAB_PIE_SICK_COLOR = "#E31A1C";

// Base on-screen diameter of a dot image at icon-size 1.0 (drawn at 2x for crispness).
// Matches the LARGEST step below, so icon-size only ever scales DOWN and dots stay sharp.

export const MAP_LAB_PIE_BASE_DIAMETER = 40;

// Dot size is a four-step ladder that DEFAULTS TO THE SMALLEST step. A dot only grows when
// its ZIP clears BOTH tests of a step: some multiple of the typical (median) count across
// the ZIPs drawn, AND a raw minimum. Ranking every ZIP against its peers — the scheme this
// replaces — spent the entire size range on ordinary variation: with a symptom filter on,
// most ZIPs sit at 1-2 reports, so every 2 took the ceiling and every 1 the floor.
//
// The multiple is what makes sizing scale-free: 10x more reports statewide moves both a
// ZIP's count and the median, so no dot changes. The raw minimum is what keeps a thin map
// looking thin — without it, 3 reports would be "3x typical" on a filtered map where the
// median is 1, and would shout about almost nothing. On the current dataset ~96% of ZIPs
// land on the floor in every view, which is the shape of the data, not a tuning artifact:
// the counts cluster tightly (p90 = 16) and then jump to a distinct outlier tail (49-132).

export const MAP_LAB_PIE_SIZE_STEPS = [
  { multiple: 8, minCount: 16, diameter: 40 },
  { multiple: 4, minCount: 8, diameter: 29 },
  { multiple: 2, minCount: 4, diameter: 22 },
];

// Diameter for a ZIP that clears no step above — on a typical map, nearly all of them.

export const MAP_LAB_PIE_FLOOR_DIAMETER = 14;

export const MAP_LAB_DYNAMIC_SOURCE_ID = "map-lab-dynamic-features";

// PREVIEW (Map Lab ZIP shading): the "Shading:" toggle picks how fill OPACITY is driven
// (color is always sick rate). "reports"/"rate" scale opacity by magnitude; "flat" (Full
// color) shows every data-bearing area at full opacity — no magnitude difference; "none"
// hides the fill entirely (used when People switch to centroid dots).

export const MAP_LAB_MAGNITUDE_METRICS = ["reports", "rate", "flat", "none"];

export const MAP_LAB_METRIC_LABELS = {
  reports: "Report volume",
  rate: "Per capita",
  flat: "Full color",
  none: "Off",
};

export const MAP_LAB_METRIC_NOTE_LABEL_KEYS = {
  reports: "map_metric_reports",
  rate: "map_metric_rate",
};

export const MAP_LAB_MIN_POPULATION = 100;

export const MAP_LAB_NODATA_COLOR = "#e5e7eb";

export const MAP_LAB_MIN_OPACITY = 0.05;
// PREVIEW (Map Lab ZIP shading): opacity encodes each area's magnitude RELATIVE to its
// peers instead of against fixed volume thresholds, so the map reads well at any dataset
// size (50 people or 10k). Per feature: norm = clamp(sqrt(value / robustMax), 0, 1); the
// sqrt lifts the long tail off the floor, and robustMax is the 95th-percentile value (not
// the single busiest area) so one runaway ZIP can't crush everything else to the floor.
// norm is stamped at join time (reports_norm / rate_norm) and mapped 0..1 -> floor..ceil.

export const MAP_LAB_SHADE_MIN_OPACITY = 0.3;

export const MAP_LAB_SHADE_MAX_OPACITY = 0.9;

export const MAP_LAB_ROBUST_MAX_PERCENTILE = 0.95;
// People centroid dots render two ways from the same dynamic GeoJSON source: manual
// aggregate bubbles or one dot per ZIP centroid. Only one layer set is visible at a time,
// chosen by the "Dots" clustered/not-clustered sub-toggle.

export const MAP_LAB_CLUSTERED_DOT_LAYER_IDS = ["people-clusters", "people-cluster-single-points"];
// PREVIEW (Map Lab county shading): the shading fill can render at two geography
// levels. "county" (default) is the calm 15-county overview; clicking a county (or
// the "ZIP" toggle) drops to the per-ZIP choropleth. Both reuse the same paint
// expressions and magnitude toggle; opacity is normalized within each level's own
// feature set at join time, so the two levels share one 0..1 opacity mapping.

export const MAP_LAB_SHADE_LEVELS = ["county", "zip"];

export const MAP_LAB_SHADE_LEVEL_LABELS = { county: "County", zip: "ZIP" };
// PREVIEW (Map Lab shading): color-scale mode toggle. "smooth" (default) is the
// continuous yellow→orange→red sick-rate ramp; "banded" collapses each area to one of
// three colors by its percentile in the sick-rate spread (~bottom 25% yellow, middle
// 50% orange, top 25% dark red), computed per geography level at join time. Only how
// color is drawn changes — opacity (magnitude) and suppression are untouched.

export const MAP_LAB_COLOR_MODES = ["smooth", "banded"];

export const MAP_LAB_COLOR_MODE_LABELS = { smooth: "Gradient", banded: "3 bands" };
// The three discrete band colors (ColorBrewer YlOrRd), shared by the banded fill
// expression and the banded legend swatch. Order = low → high sick rate.

export const MAP_LAB_BAND_COLORS = ["#fecc5c", "#fd8d3c", "#bd0026"];
// Percentile cut points splitting the sick-rate spread into the ~25/50/25 bands: areas
// below the 25th pct are yellow, at/above the 75th are dark red, the rest orange.

export const MAP_LAB_BAND_LOWER_PERCENTILE = 0.25;

export const MAP_LAB_BAND_UPPER_PERCENTILE = 0.75;

export const MAP_LAB_ZIP_SHADING_LAYER_IDS = ["arizona-zip-fills", "arizona-zip-boundaries", "arizona-zip-labels"];
// PREVIEW (Map Lab): sprite id + layer id for the "no ZIP code here" diagonal hatch.

export const MAP_LAB_NON_ZIP_HATCH_IMAGE = "non-zip-hatch";

export const MAP_LAB_NON_ZIP_LAYER_ID = "arizona-non-zip-hatch";
// "Spotlight" mask: the flattened base map keeps a thin layer of context around Arizona
// (water, parks, interstates, boundary lines — see flattenBaseMap in src/map/index.js),
// and we push it back so the state stays the subject. Two plain layers (no holes — a
// mask polygon made Mapbox's tessellator produce gray artifacts inside the state along
// the coincident county borders):
//   1. a translucent gray rectangle over the whole pannable area, dimming the neighboring
//      states' geography to a faint ghost without erasing it, then
//   2. the county polygons repainted opaque and slightly brighter ON TOP, restoring
//      Arizona as the one clean, bright surface on the map.
// Both are plain fills, so there are no hole-tessellation artifacts, and the edge where
// the light state meets the gray traces the exact state outline for free. Both sit above
// the base map's symbol (label) layers but below all app data layers.

export const MAP_LAB_OUTSIDE_MASK_SOURCE_ID = "arizona-outside-mask";

export const MAP_LAB_OUTSIDE_MASK_LAYER_ID = "arizona-outside-mask-fill";

// Neighboring-state ZIP outlines, drawn BEFORE the veil so the veil dims them along with
// the rest of the surroundings — added after it they would out-shout Arizona's own ZIPs.
// Thin and mid-gray: the shapes should be legible as ZIP-sized parcels without inviting a
// click, since there is no data behind them.

export const MAP_LAB_NEIGHBOR_ZIP_SOURCE_ID = "neighbor-zctas";

export const MAP_LAB_NEIGHBOR_ZIP_LAYER_ID = "neighbor-zcta-boundaries";

export const MAP_LAB_NEIGHBOR_ZIP_LINE_COLOR = "#8b96a5";

export const MAP_LAB_NEIGHBOR_ZIP_LINE_WIDTH = ["interpolate", ["linear"], ["zoom"], 4, 0.3, 8, 0.8];

// Cool slate rather than a pale gray: a thin veil of a darker tone pushes the surroundings
// back as far as a thick veil of a light one, but scales down the base map's own contrast
// far less (composite contrast scales with 1 - opacity), so the neighboring geography stays
// legible underneath. The cool cast also separates the surroundings from Arizona's
// warm-neutral fill.
export const MAP_LAB_OUTSIDE_MASK_COLOR = "#94a3b8";

// Partial, not opaque: enough to keep out-of-state geography recessive, light enough that
// neighboring detail (state lines, interstates, rivers, city labels) still reads underneath.
export const MAP_LAB_OUTSIDE_MASK_OPACITY = 0.28;

export const MAP_LAB_STATE_FILL_SOURCE_ID = "arizona-state-fill";

export const MAP_LAB_STATE_FILL_LAYER_ID = "arizona-state-fill";
// A touch brighter than FLAT_BASE_COLOR in src/map/index.js: the surroundings now carry
// muted geography, so Arizona earns the spotlight by being the brightest, cleanest surface
// rather than by being the only non-gray one.

export const MAP_LAB_STATE_FILL_COLOR = "#fbfbf9";
// Generous rectangle covering the whole pannable area (maxBounds is well inside this) so
// the mask never reveals an un-dimmed corner when panned to an edge. Closed ring.

export const MAP_LAB_OUTSIDE_MASK_OUTER_RING = [
  [-130, 20],
  [-100, 20],
  [-100, 45],
  [-130, 45],
  [-130, 20],
];
// County population = sum of member-ZCTA populations (raw ingredients, not rates).

export const COUNTY_POPULATION = (() => {
  const totals = {};
  Object.entries(ZIP_COUNTY).forEach(([zip, county]) => {
    const population = Number.isFinite(ZCTA_POPULATION[zip]) ? ZCTA_POPULATION[zip] : 0;
    totals[county] = (totals[county] || 0) + population;
  });
  return totals;
})();

export const ARIZONA_BOUNDS = [
  [-114.85, 31.25],
  [-109.0, 37.05],
];


export const mapInstances = new Map();

export const MAP_LAB_LAYERS = [
  { category: "people", labelKey: "map_people", swatch: MAP_LAB_PIE_SICK_COLOR },
  { category: "animals", labelKey: "map_animals", swatch: MAP_LAB_POINT_COLORS.animals },
  { category: "environment", labelKey: "map_environment", swatch: MAP_LAB_POINT_COLORS.environment },
];


export const CATEGORY_LAYER_IDS = {
  people: [],
  animals: ["animals-clusters", "animals-cluster-single-points", "animals-points"],
  environment: ["environment-clusters", "environment-cluster-single-points", "environment-points"],
};


export const MAP_LAB_PEOPLE_MODES = ["dots", "shading"];

export const MAP_LAB_PEOPLE_MODE_LABELS = { shading: "ZIP shading", dots: "Outbreak dots" };


export const MAP_LAB_POINT_TITLE_KEYS = {
  animals: "map_animal_report",
  environment: "map_environment_report",
};
